import { MapFeatureColors } from '../../core/map-object-colors';
import { Injectable, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { interval, map, Observable, Subscription } from 'rxjs';
import { SelectableFeature } from '../../core/selectable-feature';
import { PopoverFeature } from '../../core/popover-feature';
import { GridObjectSelection } from '../../core/grid-object-selection';
import { ProspectGridLayer } from './prospect-grid-layer';
import {
  GridFeatureBase,
  GridMapObject,
} from '../../core/base/grid-feature.base';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapObjectPopover } from '../../core/map-object-popover';
import { latLngToTile, tileBoundsAtZoom } from '../../core/spatial/tile-math';

import { PlacePullService } from '../../../services/place-pull.service';
import { MapViewport } from '../../core/spatial/map-viewport';
import { PullCoverageStyles } from './pull-coverage-style';

export type ProspectCellMapObject = GridMapObject;

/**
 * Prospect layer: the area-prospecting workflow (paint grid cells, then pull
 * places for them). A first-class map layer alongside the Place features, with its
 * own control panel (prospect-control). Its renderer is private to the feature;
 * GridObjectSelection owns only the selected cell identities.
 *
 * Layer visibility controls the grid lines and selection fills together.
 */
@Injectable({ providedIn: 'root' })
export class ProspectFeature extends GridFeatureBase<ProspectCellMapObject>
implements OnDestroy, SelectableFeature<ProspectCellMapObject>, PopoverFeature<ProspectCellMapObject> {
  override key = 'prospect';
  override name = 'Lead Discovery';
  override get objects(): ProspectCellMapObject[] {
    return this.selection.cells.map(cell => ({
      id: `${cell.zoom}:${cell.tileX}:${cell.tileY}`,
      ...cell,
    }));
  }
  // Secondary: dim the basemap outside the selection (spotlight). Off by default.
  dimSurroundings = false;
  override icon = 'radar';
  override iconColor = MapFeatureColors.prospects;
  override readonly page = '/prospect';

  private readonly renderer: ProspectGridLayer;
  private map?: L.Map;
  readonly coverageStyles = PullCoverageStyles;
  coverageMessage = '';
  private viewport?: MapViewport;
  private coverageRequest?: Subscription;
  private coverageTimer?: Subscription;
  private popoversEnabled = false;
  private readonly subscriptions = new Subscription();
  private readonly onUnload = () => this.detach();

  readonly selection = new GridObjectSelection();
  get selectedObjectIds(): readonly string[] { return this.selection.ids; }

  constructor(
    private readonly navigation: MapNavigationService,
    private readonly pulls: PlacePullService,
  ) {
    super();
    this.renderer = new ProspectGridLayer(this.selection);
    this.subscriptions.add(this.selection.changed$.subscribe(() => {
      if (this.focusedCell) navigation.updateFocusPopup(this.focusedCell.target, this.cellPopup(this.focusedCell.cell));
    }));
  }

  resetSession(): void {
    this.coverageRequest?.unsubscribe();
    this.viewport = undefined;
    this.coverageMessage = '';
    this.renderer.setCoverage([]);
    this.focusedCell = undefined;
    if (this.navigation.hasObjectFocus(this.key)) this.navigation.clearFocus();
    this.disablePopovers();
    this.selection.clear();
    this.clear();
  }

  attach(map: L.Map): void {
    if (this.map) this.detach();
    this.map = map;
    if (this.enabled) this.render();
    map.on('unload', this.onUnload);
    this.coverageTimer = interval(15000).subscribe(() => this.refreshCoverage());
  }

  private detach(): void {
    this.coverageTimer?.unsubscribe();
    this.map?.off('unload', this.onUnload);
    this.clear();
    this.map = undefined;
  }

  ngOnDestroy(): void {
    this.detach();
    this.subscriptions.unsubscribe();
  }

  // Visibility (the eye) = whether the grid overlay is in this layer's group, so
  // hiding the layer hides everything it draws — selection fills included.
  override render(): void {
    if (!this.enabled || !this.map) return;
    this.renderer.setDim(this.dimSurroundings);
    if (!this.layer.hasLayer(this.renderer)) this.layer.addLayer(this.renderer);
    this.refreshCoverage();
  }
  override clear(): void {
    this.coverageRequest?.unsubscribe();
    super.clear();
  }

  onViewportChanged(viewport: MapViewport): void {
    this.viewport = viewport;
    this.refreshCoverage();
  }

  refreshCoverage(): void {
    const bounds = this.viewport;
    if (!bounds || !this.enabled || !this.map || !this.layer.hasLayer(this.renderer)) return;
    this.coverageRequest?.unsubscribe();
    const wrap = (lng: number) => ((lng + 180) % 360 + 360) % 360 - 180;
    const world = bounds.east - bounds.west >= 360;
    this.coverageRequest = this.pulls.coverage({
      south: Math.max(-90, bounds.south), north: Math.min(90, bounds.north),
      west: world ? -180 : wrap(bounds.west), east: world ? 180 : wrap(bounds.east),
    }).subscribe({
      next: result => {
        this.renderer.setCoverage(result.cells);
        this.coverageMessage = result.truncated ? 'Some recorded grids are hidden. Zoom in to see all coverage.' : '';
        if (this.focusedCell) this.navigation.updateFocusPopup(this.focusedCell.target, this.cellPopup(this.focusedCell.cell));
        this.notifyChanged();
      },
      error: () => {
        this.coverageMessage = 'Could not refresh recorded coverage. Retrying…';
        this.notifyChanged();
      },
    });
  }

  /** Panel "dim surroundings" option. */
  setDimSurroundings(on: boolean): void {
    this.dimSurroundings = on;
    this.renderer.setDim(on);
  }

  setCellZoom(zoom: number): void {
    this.renderer.setCellZoom(zoom);
  }
  setColor(color: string): void { this.renderer.setColor(color); }
  get cellZoomLevel(): number { return this.renderer.cellZoomLevel; }
  get color(): string { return this.renderer.color; }

  // ── SelectableFeature: the grid is this feature's selection surface ───────
  readonly selectionCount$: Observable<number> =
    this.selection.changed$.pipe(
      map(() => this.selection.count),
    );

  enableSelection(): void {}
  disableSelection(): void {}

  enablePopovers(): void { this.popoversEnabled = true; }

  disablePopovers(): void { this.popoversEnabled = false; }

  objectIdsAt(latlng: L.LatLng): readonly string[] {
    if (!this.isGridInteractive()) return [];
    const zoom = this.renderer.cellZoomLevel;
    const cell = latLngToTile(latlng.lat, latlng.lng, zoom);
    return [`${zoom}:${cell.tileX}:${cell.tileY}`];
  }

  isObjectSelected(id: string | number): boolean { return typeof id === 'string' && this.selection.has(id); }

  setObjectsSelected(ids: readonly (string | number)[], selected: boolean): void {
    const cells = ids.filter((id): id is string => typeof id === 'string');
    if (selected) this.selection.append(cells);
    else this.selection.remove(cells);
  }

  selectObjectById(id: string | number): void { this.selection.append([String(id)]); }
  appendObjectSelection(ids: readonly (string | number)[]): void { this.selection.append(ids.map(String)); }
  toggleObjectSelection(ids: readonly (string | number)[]): void {
    for (const id of ids.map(String)) {
      if (this.selection.has(id)) this.selection.remove([id]);
      else this.selection.append([id]);
    }
  }
  inspectObject(id: string | number): void {
    const object = this.objects.find(cell => cell.id === id);
    if (object) this.focusObject(object);
  }

  showPopoverAt(latlng: L.LatLng): void {
    if (!this.popoversEnabled || !this.map || !this.isGridInteractive()) return;
    const zoom = this.renderer.cellZoomLevel;
    const cell = latLngToTile(latlng.lat, latlng.lng, zoom);
    this.focusObject({ id: `${zoom}:${cell.tileX}:${cell.tileY}`, zoom, ...cell }, latlng);
  }

  private focusedCell?: { cell: GridMapObject; target: import('../../core/map-object-focus').MapFocusObject };

  focusObject(cell: GridMapObject, anchor?: L.LatLng): void {
    const bounds = tileBoundsAtZoom(cell.tileX, cell.tileY, cell.zoom);
    const target = {
      reference: { featureKey: this.key, objectId: cell.id },
      geometry: L.rectangle(bounds).toGeoJSON(), popup: this.cellPopup(cell), anchor,
    };
    this.focusedCell = { cell, target };
    void this.navigation.focus(target);
  }

  private cellPopup({ zoom, tileX, tileY }: GridMapObject): MapObjectPopover {
    const n = 2 ** zoom;
    const coverage = this.coverageStyles.flatMap(style => {
      const cells = this.renderer.coverage.filter(c => {
        const cn = 2 ** c.zoom;
        return c.source === style.source && c.tileX / cn < (tileX + 1) / n &&
          (c.tileX + 1) / cn > tileX / n && c.tileY / cn < (tileY + 1) / n && (c.tileY + 1) / cn > tileY / n;
      });
      if (!cells.length) return [];
      const latest = cells.reduce((a, b) => a.lastPulledAt > b.lastPulledAt ? a : b);
      return [{ label: style.label + ' (overlapping pull)', value: new Date(latest.lastPulledAt).toLocaleString() }];
    });
    return {
      type: 'Grid cell', title: `${zoom}/${tileX}/${tileY}`,
      values: [
        { label: 'Selected', value: this.selection.hasCell(zoom, tileX, tileY) },
        ...coverage,
        { label: 'Zoom', value: zoom }, { label: 'Column', value: tileX }, { label: 'Row', value: tileY },
      ],
    };
  }

  selectInBounds(bounds: L.LatLngBounds): void {
    if (this.isGridInteractive()) {
      this.selection.appendBounds(bounds, this.renderer.cellZoomLevel);
    }
  }
  clearSelection(): void { this.selection.clear(); }

  private isGridInteractive(): boolean {
    return this.enabled && this.renderer.canSelect();
  }
}
